import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, Modal, ModalOverlay } from '@/components/_untitled/application/modals/modal';
import { Button } from '@/components/_untitled/base/buttons/button';
import { ArrowLeft, X } from '@untitledui/icons';
import { SuccessStep } from './steps/SuccessStep';
import { StepActions } from './StepActions';
import { useKycFlow } from '../hooks/useKycFlow';
import type { KycData, KycStepActions } from '../config/kycSteps.config';
import { getStepById } from '../config/kycSteps.config';
import { ProgressBar } from "@/components/_untitled/base/progress-indicators/progress-indicators.tsx";
import { getAssetUrl } from "@/utils/assetUrl.ts";
import { prefixes, useTypedTranslation } from "@/locales";

interface KycModalProps {
    isOpen: boolean;
    onClose: () => void;
    trigger?: string;
    auctionHouseSlug?: string;
    initialData?: KycData;
    initialStepId?: string;
}

export function KycModal({ isOpen, onClose, initialData, initialStepId }: KycModalProps) {
    const { t } = useTranslation();
    const [tGeneral] = useTypedTranslation(prefixes('general'));
    const kyc = useKycFlow(initialData, initialStepId);
    const currentStep = kyc.currentStepId === 'complete' ? null : getStepById(kyc.currentStepId);
    const [stepActions, setStepActions] = useState<KycStepActions | null>(null);

    const handleActionsChange = useCallback((actions: KycStepActions) => {
        setStepActions(actions);
    }, []);

    const handleComplete = useCallback((updates?: KycData) => {
        kyc.moveToNextIncompleteStep(updates);
    }, [kyc.moveToNextIncompleteStep]);

    const handleSkip = useCallback(() => {
        const currentIndex = kyc.steps.findIndex((s) => s.id ===
            kyc.currentStepId);
        if (currentIndex < kyc.steps.length - 1) {
            // Move to next step
            kyc.setCurrentStepId(kyc.steps[currentIndex + 1].id);
        } else {
            // Last step - go to complete
            kyc.setCurrentStepId('complete');
        }
    }, [kyc]);

    const handleBack = useCallback(() => {
        // Special handling for standalone steps (like forgot-password)
        if (currentStep?.standalone) {
            kyc.setCurrentStepId('auth-method');
            return;
        }

        const currentIndex = kyc.steps.findIndex((s) => s.id === kyc.currentStepId);
        if (currentIndex > 0) {
            kyc.setCurrentStepId(kyc.steps[currentIndex - 1].id);
        }
    }, [kyc, currentStep]);

    // Check if we can go back to the previous step
    const currentIndex = kyc.steps.findIndex((s) => s.id === kyc.currentStepId);
    const previousStep = currentIndex > 0 ? kyc.steps[currentIndex - 1] : null;

    const dialogLabel = currentStep ? t(currentStep.title) || 'KYC Process' : 'KYC Process';

    return (
        <ModalOverlay isOpen={ isOpen } onOpenChange={ (open) => !open && onClose() } isDismissable>
            <Modal>
                <Dialog aria-label={ dialogLabel }>
                        <div className="bg-white rounded-2xl overflow-hidden shadow-xl max-w-xl w-full">
                            { kyc.currentStepId === 'complete' ? (
                                <SuccessStep
                                    data={ kyc.kycData }
                                    onUpdate={ kyc.updateKycData }
                                    onComplete={ onClose }
                                />
                            ) : currentStep ? (
                                <>
                                    <div className="p-2 mb-4">
                                        <div className="relative h-32 w-full overflow-hidden rounded-t-xl mb-2">
                                            <img
                                                src={ getAssetUrl('kyc_header.png') || '' }
                                                alt="Auction room"
                                                className="h-full w-full object-cover object-[0_70%]"
                                            />
                                            <Button
                                                color="tertiary"
                                                onClick={ onClose }
                                                className="absolute top-2 end-2 text-white hover:bg-white/20 hover:text-white"
                                                iconLeading={ <X size={ 20 }/> }
                                                aria-label="Close KYC modal"
                                            />
                                        </div>

                                        {/* Hide progress bar for standalone steps */ }
                                        { !currentStep?.standalone && (
                                            <ProgressBar value={ kyc.progress || 25 }
                                                         progressClassName="bg-warning-400"/>
                                        ) }
                                    </div>
                                    { import.meta.env.VITE_ENV_TYPE === 'development' && (
                                        <Button
                                            color="secondary"
                                            onClick={ handleSkip }
                                            size="sm"
                                            className="absolute top-2 start-2 text-white bg-yellow-500 hover:bg-yellow-600"
                                            iconLeading={ <ArrowLeft size={ 20 } /> }
                                            aria-label="Skip"
                                        />
                                    )}

                                    <div className="pt-2 px-10 pb-6">
                                        <div className="flex flex-col gap-6">
                                            <header className="flex flex-col text-center gap-1">
                                                <h2 className="text-3xl font-normal text-gray-900">
                                                    { t(currentStep.title) }
                                                </h2>
                                                <p className="text-md text-gray-600 whitespace-pre-line">
                                                    { t(currentStep.description) }
                                                </p>
                                            </header>
                                            <currentStep.component
                                                data={ kyc.kycData }
                                                onUpdate={ kyc.updateKycData }
                                                onComplete={ handleComplete }
                                                onBack={ handleBack }
                                                onActionsChange={ currentStep.standalone ? undefined : handleActionsChange }
                                                onNavigateToStep={ kyc.setCurrentStepId }
                                            />
                                        </div>
                                        { currentStep.canSkip && (
                                            <Button
                                                onClick={ () => handleComplete({ addressSkipped: true }) }
                                                color="tertiary"
                                                className="underline text-center w-full -mt-4"
                                            >
                                                { tGeneral('skipKyc') }
                                            </Button>
                                        ) }
                                    </div>

                                    { stepActions && !currentStep?.standalone && (
                                        <StepActions
                                            { ...stepActions }
                                            showBack={ stepActions.showBack && (previousStep?.allowBack !== false) }
                                            onBack={ handleBack }
                                        />
                                    ) }
                                </>) : null }
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>
    );
}
